import { trackError } from '@iheartjane/tracking';
import { useEffect } from 'react';

import type { Loadable } from '@jane/monolith/lib/loadable';
import { request } from '@jane/monolith/lib/request';
import { useSafeState } from '@jane/shared/hooks';

/**
 * Fetch a source from the server at mount time and manage it as state.
 */

interface Options {
  manual?: boolean;
  /** @see https://developer.mozilla.org/en-US/docs/Web/HTTP/CORS#simple_requests */
  simple?: boolean;
  trackErrors?: boolean;
}

/** Represents an API endpoint */
interface Source {
  requireAuth?: boolean;
  url: string;
}

export const useSource = <T>(
  source: null | Source,
  { trackErrors = true, manual = false, simple = false }: Options = {}
): Loadable<T> => {
  const initResponse: Loadable<T> = {
    loading: false,
    data: undefined,
    error: undefined,
    fetch: () => {
      return;
    },
  };

  const [response, setResponse] = useSafeState<Loadable<T>>(initResponse);

  useEffect(() => {
    if (!source) return;

    const fetcher = async (): Promise<void> => {
      setResponse((prev) => ({ ...prev, loading: true }));

      const { url, requireAuth = false } = source;

      try {
        const data = await request(
          url,
          { requireAuth, method: 'GET' },
          undefined,
          simple
        );

        setResponse({
          loading: false,
          data,
          error: undefined,
          fetch: fetcher,
        });
      } catch (error) {
        trackErrors && trackError(error as Error, { url });
        setResponse({
          error: error as Error,
          loading: false,
          data: undefined,
          fetch: fetcher,
        });
      }
    };

    if (manual) {
      setResponse((prev) => ({ ...prev, fetch: fetcher }));
      return;
    }

    fetcher();
  }, [manual, source && source.url]);

  return response;
};
